"use client";
// ===================================================================
// בורר המשפחות — שורת הסוג, לצד שורת הפלטפורמות (DEC-0362, Rule 32).
//
// שני צירים, שתי שורות, ובכוונה לא בורר אחד: פלטפורמה היא תחום העבודה
// שבו אני נמצא עכשיו, ומשפחה היא סוג הדבר שאני מסתכל עליו. דחיסה של
// שניהם לשורה אחת הייתה מכריחה בחירה בין שתי שאלות שונות (Rule 36 §3).
//
// השמות אינם קבועים בקוד — הם שורות במסד, ניתנות לעריכה מן העורך של
// הגדרות הישויות. גל ביקש לשלוט בהם בעצמו, ולכן הקומפוננטה מציגה מה
// שהמסד מחזיר ואינה מתרגמת ואינה מייפה.
// ===================================================================

import { Archive, CircleDashed, Shapes } from "lucide-react";
import {
  ALL_FAMILIES,
  ARCHIVED,
  UNTAGGED_FAMILY,
  type FamilyFilter,
} from "@/lib/families/useFamilyFilter";
import type { Language } from "@/contexts/SettingsContext";

interface Props {
  filter: FamilyFilter;
  language: Language;
  /** "compact" למסילת ה-Sidebar, "full" לכותרת ה-Launchpad. */
  variant?: "compact" | "full";
  className?: string;
}

export function FamilySelector({ filter, language, variant = "compact", className = "" }: Props) {
  const { families, selected, select, totals } = filter;

  // אין בין מה לבחור עד שהמרשם מגיע. שבב "הכל" לבדו היה נראה
  // כאילו המשפחות אבדו, ולא כאילו הן עוד לא נטענו.
  if (families.length === 0) return null;

  const allLabel = language === "he" ? "הכל" : language === "ru" ? "Все" : "All";
  const untaggedLabel =
    language === "he" ? "ללא סיווג" : language === "ru" ? "Без типа" : "Unclassified";
  const untaggedHint =
    language === "he"
      ? "אפליקציות שאיש עדיין לא סיווג למשפחה"
      : "Apps nobody has classified into a family yet";
  const archivedLabel = language === "he" ? "ארכיון" : language === "ru" ? "Архив" : "Archive";
  const archivedHint =
    language === "he"
      ? "אפליקציות שנסגרו — נשארות נגישות, רק לא מציקות"
      : "Apps that were closed — still reachable, just out of the way";

  const compact = variant === "compact";
  const untaggedActive = selected === UNTAGGED_FAMILY;
  const archivedActive = selected === ARCHIVED;

  return (
    <div
      data-cc-id="sidebar.family-selector"
      className={`flex flex-wrap items-center gap-0.5 rounded-lg bg-slate-800/50 p-0.5 ${className}`}
      role="group"
      aria-label={language === "he" ? "בורר משפחת אפליקציות" : "App family selector"}
    >
      {[{ id: "__all__", slug: ALL_FAMILIES, title: allLabel, appCount: totals.all }, ...families].map(
        (f) => {
          const isActive = selected === f.slug;
          const count = f.appCount;
          return (
            <button
              key={f.slug}
              type="button"
              onClick={() => select(f.slug)}
              aria-pressed={isActive}
              title={count == null ? f.title : `${f.title} — ${count} App`}
              className={`min-w-0 rounded-md font-medium transition-colors ${
                compact ? "flex-1 px-1 py-1 text-[10px]" : "px-2.5 py-1.5 text-xs"
              } ${
                isActive
                  ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <span className="flex min-w-0 items-center justify-center gap-1">
                {f.slug === ALL_FAMILIES && !compact && <Shapes className="h-3 w-3 shrink-0" />}
                <span className="truncate">{f.title}</span>
                {count != null && (
                  // shrink-0 כדי שהמספר ישרוד כשהכותרת נחתכת — שבב שכתוב
                  // בו "תוכנות צד שלי…" בלי מספר מאבד את מה שהוא נועד לו.
                  <span className="shrink-0 tabular-nums opacity-60">{count}</span>
                )}
              </span>
            </button>
          );
        },
      )}

      {/* שני השבבים האלה אינם משפחות. האחד הוא מה שנשאר בלי החלטה,
          והשני הוא מה שנסגר — שני מצבים, לא שני סוגים. */}
      <div
        className={`flex items-center gap-0.5 ${
          compact ? "basis-full border-t border-slate-700/60 mt-0.5 pt-1" : ""
        }`}
      >
        <button
          type="button"
          onClick={() => select(untaggedActive ? ALL_FAMILIES : UNTAGGED_FAMILY)}
          aria-pressed={untaggedActive}
          title={totals.untagged == null ? untaggedHint : `${untaggedHint} — ${totals.untagged} App`}
          className={`min-w-0 flex-1 truncate rounded-md border border-dashed font-medium transition-colors ${
            compact ? "px-1 py-1 text-[10px]" : "px-2.5 py-1.5 text-xs"
          } ${
            untaggedActive
              ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
              : "text-slate-500 hover:text-slate-300 border-slate-700/60"
          }`}
        >
          <span className="inline-flex min-w-0 items-center gap-1">
            <CircleDashed className="h-3 w-3 shrink-0" />
            <span className="truncate">{untaggedLabel}</span>
            {totals.untagged != null && (
              <span className="shrink-0 tabular-nums opacity-60">{totals.untagged}</span>
            )}
          </span>
        </button>

        <button
          type="button"
          onClick={() => select(archivedActive ? ALL_FAMILIES : ARCHIVED)}
          aria-pressed={archivedActive}
          title={totals.archived == null ? archivedHint : `${archivedHint} — ${totals.archived} App`}
          className={`min-w-0 flex-1 truncate rounded-md border font-medium transition-colors ${
            compact ? "px-1 py-1 text-[10px]" : "px-2.5 py-1.5 text-xs"
          } ${
            archivedActive
              ? "bg-slate-500/20 text-slate-200 border-slate-400/40"
              : "text-slate-500 hover:text-slate-300 border-slate-700/60"
          }`}
        >
          <span className="inline-flex min-w-0 items-center gap-1">
            <Archive className="h-3 w-3 shrink-0" />
            <span className="truncate">{archivedLabel}</span>
            {totals.archived != null && (
              <span className="shrink-0 tabular-nums opacity-60">{totals.archived}</span>
            )}
          </span>
        </button>
      </div>
    </div>
  );
}
